import clsx from "clsx";
import type { ReactNode } from "react";

export type EmptyStateProps = {
  /** A headline in Foundling's voice — never "No results". */
  title: string;
  /** One or two sentences that tell the reader what to do next. */
  children?: ReactNode;
  /** A primary action, if there is an obvious one. */
  action?: ReactNode;
  /** The pressed-specimen silhouette drawn behind the text. */
  motif?: "sheet" | "sprig" | "list" | "search";
  className?: string;
};

const MOTIFS: Record<NonNullable<EmptyStateProps["motif"]>, ReactNode> = {
  // A blank herbarium sheet with its label block ruled and waiting.
  sheet: (
    <>
      <rect x="14" y="8" width="68" height="80" fill="var(--color-card)" stroke="var(--color-rule)" />
      <path d="M28 24h26M28 32h40M28 40h34" stroke="var(--color-rule)" strokeDasharray="3 3" />
      <rect x="50" y="62" width="28" height="18" stroke="var(--color-rule)" fill="none" />
      <path d="M55 69h18M55 74h11" stroke="var(--color-rule)" />
    </>
  ),
  // A pressed sprig, the shape of the thing you haven't collected yet.
  sprig: (
    <>
      <path d="M48 88V22" stroke="var(--color-moss)" strokeWidth="2" strokeLinecap="round" />
      <path d="M48 46c0-13 10-23 26-25-1 15-11 25-26 25Z" fill="none" stroke="var(--color-ochre)" />
      <path d="M48 66c0-13-10-23-26-25 1 15 11 25 26 25Z" fill="none" stroke="var(--color-teal)" />
      <circle cx="48" cy="17" r="6" fill="none" stroke="var(--color-rust)" />
    </>
  ),
  // A checklist with nothing ticked.
  list: (
    <>
      <rect x="18" y="14" width="60" height="68" fill="var(--color-card)" stroke="var(--color-rule)" />
      <rect x="26" y="26" width="9" height="9" stroke="var(--color-rule)" fill="none" />
      <rect x="26" y="44" width="9" height="9" stroke="var(--color-rule)" fill="none" />
      <rect x="26" y="62" width="9" height="9" stroke="var(--color-rule)" fill="none" />
      <path d="M42 31h28M42 49h22M42 67h26" stroke="var(--color-rule)" strokeDasharray="3 3" />
    </>
  ),
  // A hand lens over nothing in particular.
  search: (
    <>
      <circle cx="43" cy="42" r="24" fill="var(--color-card)" stroke="var(--color-rule)" />
      <circle cx="43" cy="42" r="17" fill="none" stroke="var(--color-rule)" strokeDasharray="3 4" />
      <path d="M61 60 78 78" stroke="var(--color-ochre)" strokeWidth="3" strokeLinecap="round" />
    </>
  ),
};

/**
 * An empty list is a first-run experience, not a shrug. Every one of these has
 * a real headline, a real sentence, and where possible a way forward.
 */
export function EmptyState({ title, children, action, motif = "sheet", className }: EmptyStateProps) {
  return (
    <section
      className={clsx(
        "sheet grain-strong relative mx-auto flex max-w-md flex-col items-center px-6 py-10 text-center",
        className,
      )}
    >
      <svg
        viewBox="0 0 96 96"
        aria-hidden="true"
        className="mb-5 h-24 w-24 opacity-80"
        fill="none"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        {MOTIFS[motif]}
      </svg>

      <h2 className="display-4 text-2xl leading-tight text-ink">
        {title}
      </h2>

      {children ? (
        <div className="mt-3 max-w-prose font-prose text-[1.0625rem] leading-relaxed text-ink-soft">
          {children}
        </div>
      ) : null}

      {action ? <div className="mt-6">{action}</div> : null}
    </section>
  );
}
